import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  ChevronLeft, ChevronRight, X, Maximize2, Minimize2, 
  Play, Pause, Volume2, VolumeX, ZoomIn, ZoomOut,
  Loader2, PenTool, Check, Send, Home, FileText,
  Calculator, Video, Image, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// SIGNATURE PAD COMPONENT (In-Person Signing)
// ============================================================================

const SignaturePad = ({ onSave, onCancel, signerInfo, onSignerInfoChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height)
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSave = () => {
    if (!signerInfo.name) {
      toast.error('Please enter your name');
      return;
    }
    if (!hasSignature) {
      toast.error('Please sign in the box');
      return;
    }
    
    const signatureData = canvasRef.current.toDataURL('image/png');
    onSave(signatureData);
  };

  return (
    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg mx-auto text-slate-900">
      <h2 className="text-2xl font-bold text-center mb-6 text-slate-900">Sign Estimate</h2>
      
      <div className="space-y-4 mb-6">
        <div>
          <Label className="text-slate-700 font-semibold">Full Name *</Label>
          <Input
            value={signerInfo.name}
            onChange={(e) => onSignerInfoChange({ ...signerInfo, name: e.target.value })}
            placeholder="Enter your full name"
            className="text-lg text-slate-900 mt-1"
          />
        </div>
        <div>
          <Label className="text-slate-700 font-semibold">Email</Label>
          <Input
            type="email"
            value={signerInfo.email}
            onChange={(e) => onSignerInfoChange({ ...signerInfo, email: e.target.value })}
            placeholder="your@email.com"
            className="text-slate-900 mt-1"
          />
        </div>
      </div>

      <div className="mb-4">
        <Label className="text-slate-700 font-semibold">Signature *</Label>
        <div className="border-2 border-slate-300 rounded-lg overflow-hidden bg-white mt-2">
          <canvas
            ref={canvasRef}
            width={450}
            height={150}
            className="w-full touch-none cursor-crosshair"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
        <button
          type="button"
          onClick={clearSignature}
          className="text-sm text-blue-600 hover:underline mt-2"
        >
          Clear signature
        </button>
      </div>

      <div className="flex gap-3">
        <button 
          onClick={onCancel} 
          type="button"
          style={{
            color: '#1d4ed8',
            borderColor: '#3b82f6',
            backgroundColor: 'white',
            border: '2px solid #3b82f6'
          }}
          className="flex-1 h-10 px-4 py-2 rounded-md font-semibold hover:bg-blue-50 transition-colors"
        >
          Cancel
        </button>
        <Button onClick={handleSave} className="flex-1 h-10 bg-green-600 hover:bg-green-700 text-white">
          <Check className="h-4 w-4 mr-2" />
          Accept & Sign
        </Button>
      </div>
    </div>
  );
};

// ============================================================================
// PAGE RENDERERS
// ============================================================================

const CoverPageRenderer = ({ page, estimate, job }) => {
  const config = page.config || {};
  
  return (
    <div className="h-full w-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900 text-white relative overflow-hidden">
      {/* Background Image */}
      {config.cover_image_url && (
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${config.cover_image_url})` }}
        />
      )}
      
      {/* Content */}
      <div className="relative z-10 text-center p-8">
        <div className="mb-8">
          <img src="/rsj-logo.svg" alt="RhinoShield" className="h-16 mx-auto mb-4 invert" />
        </div>
        
        <h1 className="text-5xl font-bold mb-4">
          {config.estimate_title || estimate?.title || 'Estimate'}
        </h1>
        
        <div className="text-2xl text-slate-300 mb-8">
          {config.customer_name || job?.customer_name || 'Customer'}
        </div>
        
        <div className="text-lg text-slate-400">
          {config.address || job?.address || ''}
        </div>
        
        <div className="mt-12 text-sm text-slate-500">
          <p>Prepared by: {config.prepared_by || 'RhinoShield Florida'}</p>
          <p>{new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
};

const DetailsPageRenderer = ({ page, estimate, lineItems = [] }) => {
  const config = page.config || {};
  
  // Use estimate overrides or fall back to measurements_summary
  const measurementsSummary = estimate?.measurements_summary || {};
  const coatingSqft = estimate?.coating_sqft_override ?? measurementsSummary?.coating_sqft ?? 0;
  const repairSqft = estimate?.repair_sqft_override ?? measurementsSummary?.repair_sqft ?? 0;
  const coatingLf = measurementsSummary?.coating_lf ?? 0;
  const repairLf = measurementsSummary?.repair_lf ?? 0;
  
  // Pricing mode
  const pricingMode = estimate?.pricing_mode || 'today';
  const pricePerSqft = pricingMode === 'today' ? 9.00 : 15.00;
  const totalSqft = coatingSqft + repairSqft;
  const basePrice = totalSqft * pricePerSqft;
  
  // Line items total - only include items visible to customer
  const visibleLineItems = lineItems.filter(item => item.visible_to_customer !== false);
  const lineItemsTotal = visibleLineItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
  
  // Always calculate totals from current data (don't use potentially stale saved values)
  const calculatedSubtotal = basePrice + lineItemsTotal;
  const taxAmount = estimate?.tax_amount || 0;
  const discountAmount = estimate?.discount_amount || 0;
  const calculatedTotal = calculatedSubtotal + taxAmount - discountAmount;
  
  const showMeasurements = config.show_measurements !== false;
  
  return (
    <div className="h-full w-full overflow-y-auto bg-white p-8">
      <h2 className="text-3xl font-bold mb-8 text-center text-slate-900">Estimate Details</h2>
      
      {/* Pricing Mode Badge */}
      <div className="flex justify-center mb-6">
        <div className={`px-4 py-2 rounded-full font-medium ${
          pricingMode === 'today' 
            ? 'bg-green-100 text-green-800' 
            : 'bg-amber-100 text-amber-800'
        }`}>
          {pricingMode === 'today' ? 'Today Price' : '90 Day Price'}: ${pricePerSqft.toFixed(2)}/sqft
        </div>
      </div>
      
      {showMeasurements && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 border-b border-slate-300 pb-2 text-slate-800">Measurements</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-100 rounded-lg text-center">
              <p className="text-sm text-slate-700 font-medium">Coating Area</p>
              <p className="text-2xl font-bold text-slate-900">{coatingSqft.toLocaleString()}</p>
              <p className="text-sm text-slate-600">sqft</p>
            </div>
            <div className="p-4 bg-blue-100 rounded-lg text-center">
              <p className="text-sm text-slate-700 font-medium">Linear Feet</p>
              <p className="text-2xl font-bold text-slate-900">{coatingLf.toLocaleString()}</p>
              <p className="text-sm text-slate-600">LF</p>
            </div>
            <div className="p-4 bg-amber-100 rounded-lg text-center">
              <p className="text-sm text-slate-700 font-medium">Repair Area</p>
              <p className="text-2xl font-bold text-slate-900">{repairSqft.toLocaleString()}</p>
              <p className="text-sm text-slate-600">sqft</p>
            </div>
            <div className="p-4 bg-slate-200 rounded-lg text-center">
              <p className="text-sm text-slate-700 font-medium">Repair LF</p>
              <p className="text-2xl font-bold text-slate-900">{repairLf.toLocaleString()}</p>
              <p className="text-sm text-slate-600">LF</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Base Price Section */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold mb-4 border-b border-slate-300 pb-2 text-slate-800">Coating Services</h3>
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-300 bg-slate-100">
              <th className="text-left py-3 px-2 text-slate-800 font-semibold">Description</th>
              <th className="text-right py-3 px-2 text-slate-800 font-semibold">Area</th>
              <th className="text-right py-3 px-2 text-slate-800 font-semibold">Rate</th>
              <th className="text-right py-3 px-2 text-slate-800 font-semibold">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-200">
              <td className="py-3 px-2 text-slate-800">Rhino Shield Premium Coating</td>
              <td className="text-right py-3 px-2 text-slate-700">{totalSqft.toLocaleString()} sqft</td>
              <td className="text-right py-3 px-2 text-slate-700">${pricePerSqft.toFixed(2)}/sqft</td>
              <td className="text-right py-3 px-2 text-slate-900 font-medium">${basePrice.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Line Items Section - only show customer-visible items */}
      {visibleLineItems.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold mb-4 border-b border-slate-300 pb-2 text-slate-800">Additional Items</h3>
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-300 bg-slate-100">
                <th className="text-left py-3 px-2 text-slate-800 font-semibold">Item</th>
                <th className="text-right py-3 px-2 text-slate-800 font-semibold">Qty</th>
                <th className="text-right py-3 px-2 text-slate-800 font-semibold">Unit Price</th>
                <th className="text-right py-3 px-2 text-slate-800 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {visibleLineItems.map((item, i) => (
                <tr key={item.id || i} className="border-b border-slate-200">
                  <td className="py-3 px-2 text-slate-800">
                    {item.name}
                    {item.description && <p className="text-sm text-slate-500">{item.description}</p>}
                  </td>
                  <td className="text-right py-3 px-2 text-slate-700">{item.quantity} {item.unit}</td>
                  <td className="text-right py-3 px-2 text-slate-700">${(item.unit_price || 0).toFixed(2)}</td>
                  <td className="text-right py-3 px-2 text-slate-900 font-medium">${(item.total_price || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Pricing Summary */}
      <div className="mt-8 border-t border-slate-300 pt-6">
        <div className="max-w-sm ml-auto space-y-3">
          <div className="flex justify-between text-slate-700">
            <span>Coating Services</span>
            <span className="font-medium text-slate-900">${basePrice.toLocaleString()}</span>
          </div>
          {lineItemsTotal > 0 && (
            <div className="flex justify-between text-slate-700">
              <span>Additional Items</span>
              <span className="font-medium text-slate-900">${lineItemsTotal.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-slate-200 pt-2">
            <span className="text-slate-700">Subtotal</span>
            <span className="font-medium text-slate-900">${calculatedSubtotal.toLocaleString()}</span>
          </div>
          {taxAmount > 0 && (
            <div className="flex justify-between text-slate-700">
              <span>Tax</span>
              <span className="font-medium text-slate-900">${taxAmount.toLocaleString()}</span>
            </div>
          )}
          {discountAmount > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Discount</span>
              <span className="font-medium">-${discountAmount.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between border-t-2 border-slate-900 pt-3 text-2xl font-bold text-slate-900">
            <span>Total</span>
            <span>${calculatedTotal.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const SignaturePageRenderer = ({ page, hasSignature, onSignClick }) => {
  return (
    <div className="h-full w-full flex flex-col items-center justify-center bg-white p-8">
      <div className="max-w-md text-center">
        <PenTool className="h-16 w-16 mx-auto text-blue-600 mb-6" />
        
        <h2 className="text-3xl font-bold mb-4 text-slate-900">Signature Required</h2>
        
        {hasSignature ? (
          <div className="space-y-4">
            <div className="p-4 bg-green-100 rounded-lg border border-green-300">
              <Check className="h-8 w-8 mx-auto text-green-700 mb-2" />
              <p className="text-green-800 font-medium">Estimate Signed</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-slate-700 mb-6 text-lg">
              By signing below, you agree to the terms and conditions of this estimate.
            </p>
            
            <Button 
              size="lg" 
              onClick={onSignClick}
              className="bg-blue-600 hover:bg-blue-700 text-white text-lg px-8 py-3"
            >
              <PenTool className="h-5 w-5 mr-2" />
              Sign Now
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const WarrantyPageRenderer = ({ page, estimate }) => {
  const warranties = estimate?.warranties || [];
  
  return (
    <div className="h-full w-full overflow-y-auto bg-white p-8">
      <div className="flex items-center justify-center gap-3 mb-8">
        <div className="p-3 bg-emerald-100 rounded-full">
          <Check className="h-8 w-8 text-emerald-600" />
        </div>
        <h2 className="text-3xl font-bold text-slate-900">Warranty Protection</h2>
      </div>
      
      {warranties.length === 0 ? (
        <div className="text-center text-slate-500 py-8">
          <p>No warranty information available</p>
        </div>
      ) : (
        <div className="grid gap-6 max-w-3xl mx-auto">
          {warranties.map((warranty, i) => (
            <div key={i} className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-6 border border-emerald-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-emerald-500 rounded-lg text-white">
                  <Check className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-slate-900">{warranty.name}</h3>
                    {warranty.is_transferable && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        Transferable
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-3xl font-bold text-emerald-600">{warranty.term_years}</span>
                    <span className="text-lg text-slate-600">Year{warranty.term_years > 1 ? 's' : ''}</span>
                  </div>
                  
                  <p className="text-slate-700 mb-3">{warranty.description}</p>
                  
                  {warranty.coverage_details && (
                    <div className="mt-3 pt-3 border-t border-emerald-200">
                      <p className="text-sm text-slate-600 font-medium mb-1">Coverage Details:</p>
                      <p className="text-sm text-slate-600">{warranty.coverage_details}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-8 p-4 bg-slate-100 rounded-lg text-center">
        <p className="text-sm text-slate-600">
          Warranty coverage begins upon project completion and final payment.
        </p>
      </div>
    </div>
  );
};

// ABOUT COMPANY PAGE RENDERER (About Rhino Shield)
const AboutCompanyPageRenderer = ({ page }) => {
  const config = page.config || {};
  const features = config.features || [];
  
  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto p-8 md:p-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {config.company_name || 'About Our Company'}
          </h1>
          {config.tagline && (
            <p className="text-xl md:text-2xl text-blue-600 font-medium italic">
              {config.tagline}
            </p>
          )}
        </div>
        
        {/* Introduction */}
        {config.intro_text && (
          <div className="mb-12">
            <p className="text-lg text-slate-700 leading-relaxed text-center max-w-3xl mx-auto">
              {config.intro_text}
            </p>
          </div>
        )}
        
        {/* Features Grid */}
        {config.show_features !== false && features.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl shadow-lg p-6 border border-slate-100 hover:shadow-xl transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        )}
        
        {/* Custom Content */}
        {config.custom_content && (
          <div className="prose max-w-none text-slate-700">
            {config.custom_content}
          </div>
        )}
      </div>
    </div>
  );
};

// WARRANTY PDF PAGE RENDERER (Detailed Warranty Document)
const WarrantyPDFPageRenderer = ({ page }) => {
  const config = page.config || {};
  
  return (
    <div className="h-full w-full overflow-y-auto bg-white">
      <div className="max-w-4xl mx-auto p-8 md:p-12">
        {/* Header */}
        <div className="text-center mb-12 pb-8 border-b-2 border-blue-600">
          <Shield className="h-16 w-16 text-blue-600 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            {config.warranty_title || 'Warranty Information'}
          </h1>
          {config.coverage_intro && (
            <p className="text-lg text-slate-600 mt-4 max-w-2xl mx-auto">
              {config.coverage_intro}
            </p>
          )}
        </div>
        
        {/* What's Covered */}
        {config.show_covered_items !== false && config.covered_items?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Check className="h-6 w-6 text-green-600" />
              What&apos;s Covered
            </h2>
            <div className="bg-green-50 rounded-xl p-6">
              <ul className="space-y-3">
                {config.covered_items.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {/* Exclusions */}
        {config.show_exclusions !== false && config.exclusions?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <X className="h-6 w-6 text-red-600" />
              Exclusions
            </h2>
            <div className="bg-red-50 rounded-xl p-6">
              <ul className="space-y-3">
                {config.exclusions.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <X className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {/* Transfer Information */}
        {config.show_transfer_info !== false && config.transfer_info && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Transferability</h2>
            <div className="bg-blue-50 rounded-xl p-6">
              <p className="text-slate-700">{config.transfer_info}</p>
            </div>
          </div>
        )}
        
        {/* Claim Process */}
        {config.show_claim_process !== false && config.claim_process && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Claim Process</h2>
            <div className="bg-slate-50 rounded-xl p-6">
              <p className="text-slate-700 mb-4">{config.claim_process}</p>
              {config.contact_info && (
                <p className="text-blue-600 font-medium">{config.contact_info}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// FINANCING PAGE RENDERER
const FinancingPageRenderer = ({ page, estimate }) => {
  const financingOptions = estimate?.financing_options || [];
  const total = estimate?.total || estimate?.subtotal || 0;
  
  const calculateMonthlyPayment = (principal, apr, months) => {
    if (apr === 0) return principal / months;
    const monthlyRate = apr / 100 / 12;
    return (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) / 
           (Math.pow(1 + monthlyRate, months) - 1);
  };
  
  if (financingOptions.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-slate-100">
        <p className="text-slate-500">No financing options available</p>
      </div>
    );
  }
  
  return (
    <div className="h-full w-full overflow-y-auto bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto p-8 md:p-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Financing Options
          </h1>
          <p className="text-xl text-slate-600">
            Flexible payment plans to fit your budget
          </p>
          <p className="text-2xl font-bold text-blue-600 mt-4">
            Project Total: ${total.toLocaleString()}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {financingOptions.map((option, index) => {
            const monthlyPayment = calculateMonthlyPayment(
              total, option.apr_percent, option.term_months
            );
            
            return (
              <div 
                key={option.id || index} 
                className={`bg-white rounded-xl shadow-lg p-6 border-2 ${
                  option.is_featured 
                    ? 'border-blue-600 ring-2 ring-blue-100' 
                    : 'border-slate-100'
                }`}
              >
                {option.is_featured && (
                  <div className="text-center mb-4">
                    <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">
                  {option.plan_name}
                </h3>
                <div className="text-center mb-4">
                  <p className="text-4xl font-bold text-blue-600">
                    ${monthlyPayment.toFixed(2)}
                  </p>
                  <p className="text-slate-500">per month</p>
                </div>
                <div className="text-center text-sm text-slate-600 space-y-1">
                  <p>{option.term_months} months</p>
                  <p>{option.apr_percent}% APR</p>
                </div>
              </div>
            );
          })}
        </div>
        
        <p className="text-center text-sm text-slate-500 mt-8">
          *Financing subject to credit approval. Rates and terms may vary.
        </p>
      </div>
    </div>
  );
};

const TermsPageRenderer = ({ page }) => {
  const termsText = page.config?.terms_text || `
TERMS AND CONDITIONS

1. SCOPE OF WORK
This estimate covers the work described in the preceding pages.

2. PAYMENT TERMS
- 50% deposit required to schedule work
- Balance due upon completion

3. WARRANTY
- 25-Year Transferable Warranty on coating materials
- 5-Year Warranty on workmanship

4. SCHEDULING
Work will be scheduled upon receipt of signed estimate and deposit.

5. CUSTOMER RESPONSIBILITIES
- Ensure access to work areas
- Remove or protect personal items in work areas
`;

  return (
    <div className="h-full w-full overflow-y-auto bg-white p-8">
      <h2 className="text-3xl font-bold mb-8 text-center text-slate-900">Terms & Conditions</h2>
      <div className="prose max-w-none whitespace-pre-wrap text-slate-800 leading-relaxed">
        {termsText}
      </div>
    </div>
  );
};

const MediaPageRenderer = ({ page }) => {
  const isVideo = page.page_type === 'VIDEO';
  const isPDF = page.page_type === 'UPLOAD_PDF';
  const isImage = page.page_type === 'UPLOAD_IMAGE';
  
  const mediaUrl = page.asset_url;
  
  if (!mediaUrl) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-slate-100">
        <div className="text-center text-slate-500">
          {isVideo && <Video className="h-16 w-16 mx-auto mb-4" />}
          {isPDF && <FileText className="h-16 w-16 mx-auto mb-4" />}
          {isImage && <Image className="h-16 w-16 mx-auto mb-4" />}
          <p>No content uploaded</p>
        </div>
      </div>
    );
  }
  
  if (isVideo) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-black">
        <video
          src={mediaUrl}
          controls
          className="max-w-full max-h-full"
          autoPlay={false}
        />
      </div>
    );
  }
  
  if (isPDF) {
    return (
      <div className="h-full w-full">
        <iframe
          src={mediaUrl}
          className="w-full h-full"
          title={page.title}
        />
      </div>
    );
  }
  
  // Image
  return (
    <div className="h-full w-full flex items-center justify-center bg-slate-900 p-4">
      <img
        src={mediaUrl}
        alt={page.title}
        className="max-w-full max-h-full object-contain"
      />
    </div>
  );
};

const CustomTextPageRenderer = ({ page }) => {
  const content = page.config?.content || page.config?.text || '';
  
  return (
    <div className="h-full w-full overflow-y-auto bg-white p-8">
      <h2 className="text-3xl font-bold mb-8 text-center">{page.title}</h2>
      <div className="prose max-w-none whitespace-pre-wrap">
        {content}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PRESENT MODE COMPONENT
// ============================================================================

export const PresentMode = () => {
  const { jobId, estimateId } = useParams();
  const navigate = useNavigate();
  
  const [estimate, setEstimate] = useState(null);
  const [job, setJob] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signerInfo, setSignerInfo] = useState({ name: '', email: '' });
  const [signing, setSigning] = useState(false);
  
  const containerRef = useRef(null);
  
  // Fetch estimate and job data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [estRes, jobRes, lineItemsRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/estimates/${estimateId}`),
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/jobs/${jobId}/estimates/${estimateId}/line-items`).catch(() => ({ data: [] }))
      ]);
      setEstimate(estRes.data);
      setJob(jobRes.data);
      setLineItems(lineItemsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load estimate');
    } finally {
      setLoading(false);
    }
  }, [jobId, estimateId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get customer-visible pages only
  const visiblePages = estimate?.pages?.filter(p => p.customer_visible)
    .sort((a, b) => a.sort_order - b.sort_order) || [];

  const currentPage = visiblePages[currentPageIndex];
  const hasSignature = estimate?.signatures?.length > 0;

  const goNext = useCallback(() => {
    if (currentPageIndex < visiblePages.length - 1) {
      setCurrentPageIndex(prev => prev + 1);
    }
  }, [currentPageIndex, visiblePages.length]);

  const goPrev = useCallback(() => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
    }
  }, [currentPageIndex]);

  const handleClose = useCallback(() => {
    navigate(`/workspace/jobs/${jobId}/estimates/${estimateId}/edit`);
  }, [navigate, jobId, estimateId]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          exitFullscreen();
        } else {
          handleClose();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, isFullscreen, handleClose]);

  const goToPage = (index) => {
    setCurrentPageIndex(index);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle signature
  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/estimates/${estimateId}/sign`, {
        signer_name: signerInfo.name,
        signer_email: signerInfo.email,
        signature_data: signatureData,
        method: 'IN_PERSON'
      });
      
      toast.success('Estimate signed successfully!');
      setShowSignatureModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error signing:', error);
      toast.error('Failed to save signature');
    } finally {
      setSigning(false);
    }
  };

  // Render page content
  const renderPageContent = () => {
    if (!currentPage) return null;

    switch (currentPage.page_type) {
      case 'COVER':
        return <CoverPageRenderer page={currentPage} estimate={estimate} job={job} />;
      case 'DETAILS':
        return <DetailsPageRenderer page={currentPage} estimate={estimate} lineItems={lineItems} />;
      case 'WARRANTY':
        return <WarrantyPageRenderer page={currentPage} estimate={estimate} />;
      case 'ABOUT_COMPANY':
        return <AboutCompanyPageRenderer page={currentPage} />;
      case 'WARRANTY_PDF':
        return <WarrantyPDFPageRenderer page={currentPage} />;
      case 'FINANCING':
        return <FinancingPageRenderer page={currentPage} estimate={estimate} />;
      case 'SIGNATURE':
        return (
          <SignaturePageRenderer 
            page={currentPage} 
            hasSignature={hasSignature}
            onSignClick={() => setShowSignatureModal(true)}
          />
        );
      case 'TERMS':
        return <TermsPageRenderer page={currentPage} />;
      case 'VIDEO':
      case 'UPLOAD_PDF':
      case 'UPLOAD_IMAGE':
        return <MediaPageRenderer page={currentPage} />;
      case 'CUSTOM_TEXT':
        return <CustomTextPageRenderer page={currentPage} />;
      default:
        return (
          <div className="h-full w-full flex items-center justify-center bg-slate-100">
            <p className="text-slate-500">Unknown page type: {currentPage.page_type}</p>
          </div>
        );
    }
  };

  // Get page icon
  const getPageIcon = (pageType) => {
    switch (pageType) {
      case 'COVER': return Home;
      case 'DETAILS': return Calculator;
      case 'WARRANTY': return Shield;
      case 'ABOUT_COMPANY': return Shield;
      case 'WARRANTY_PDF': return FileText;
      case 'FINANCING': return Calculator;
      case 'SIGNATURE': return PenTool;
      case 'TERMS': return FileText;
      case 'VIDEO': return Video;
      default: return FileText;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-white" />
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">Estimate not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  if (visiblePages.length === 0) {
    return (
      <div className="fixed inset-0 bg-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <p className="text-xl mb-4">No visible pages in this estimate</p>
          <Button onClick={handleClose}>Return to Editor</Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 bg-slate-900 flex flex-col"
      data-testid="present-mode-container"
    >
      {/* Top Bar */}
      <div className="bg-black/50 backdrop-blur px-4 py-2 flex items-center justify-between z-50">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleClose}
            className="text-white hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </Button>
          
          <div className="text-white">
            <h1 className="font-semibold">{estimate.title}</h1>
            <p className="text-xs text-slate-400">{estimate.estimate_number}</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Page indicator */}
          <div className="text-white text-sm mr-4">
            Page {currentPageIndex + 1} of {visiblePages.length}
          </div>
          
          {/* Fullscreen toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white/10"
          >
            {isFullscreen ? (
              <Minimize2 className="h-5 w-5" />
            ) : (
              <Maximize2 className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Page Thumbnails Sidebar */}
        <div className="w-24 bg-black/30 backdrop-blur overflow-y-auto p-2 space-y-2">
          {visiblePages.map((page, index) => {
            const PageIcon = getPageIcon(page.page_type);
            return (
              <button
                key={page.id}
                onClick={() => goToPage(index)}
                className={`w-full aspect-[3/4] rounded-lg flex flex-col items-center justify-center transition-all ${
                  index === currentPageIndex
                    ? 'bg-blue-500 text-white ring-2 ring-blue-400'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                <PageIcon className="h-6 w-6 mb-1" />
                <span className="text-[10px] text-center px-1 line-clamp-2">
                  {page.title || page.page_type}
                </span>
                <span className="text-[10px] mt-1">{index + 1}</span>
              </button>
            );
          })}
        </div>

        {/* Main Page Display */}
        <div className="flex-1 flex items-center justify-center p-4 relative">
          {/* Previous Button */}
          <button
            onClick={goPrev}
            disabled={currentPageIndex === 0}
            className={`absolute left-4 p-3 rounded-full transition-all z-10 ${
              currentPageIndex === 0
                ? 'opacity-30 cursor-not-allowed'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            <ChevronLeft className="h-8 w-8" />
          </button>

          {/* Page Content */}
          <div className="w-full h-full max-w-5xl max-h-[80vh] bg-white rounded-lg shadow-2xl overflow-hidden">
            {renderPageContent()}
          </div>

          {/* Next Button */}
          <button
            onClick={goNext}
            disabled={currentPageIndex === visiblePages.length - 1}
            className={`absolute right-4 p-3 rounded-full transition-all z-10 ${
              currentPageIndex === visiblePages.length - 1
                ? 'opacity-30 cursor-not-allowed'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-black/50 backdrop-blur px-4 py-2 flex items-center justify-center gap-1 z-50">
        {visiblePages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToPage(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentPageIndex
                ? 'bg-blue-500 w-8'
                : 'bg-white/30 hover:bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="sm:max-w-lg p-0 overflow-hidden border-0 bg-transparent shadow-none">
          <SignaturePad
            signerInfo={signerInfo}
            onSignerInfoChange={setSignerInfo}
            onSave={handleSign}
            onCancel={() => setShowSignatureModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Keyboard hint */}
      <div className="fixed bottom-16 left-4 text-white/50 text-xs">
        ← → Navigate | Space Next | F Fullscreen | Esc Exit
      </div>
    </div>
  );
};

export default PresentMode;
